import React from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import FieldError from './FieldError';

// ============================================================
// CommonDropdown
//
// Reusable single-select dropdown.
//   - Gray 12px label (with optional red asterisk)
//   - 16px black value
//   - MUI Paper menu: bottom rounded corners (16px) + soft shadow
//   - Tick mark on the currently selected option
//   - Chevron + icons in brand blue (#1460AA)
//   - Inline red error text below
//
// `options`: Array<{ value: string, label: string }>
// Controlled: pass `value` + `onChange(nextValue)`.
// ============================================================

const labelSx = {
  color: '#6D6D6F',
  fontSize: '12px',
  lineHeight: '16px',
  letterSpacing: 0,
  '&.Mui-focused': { color: '#6D6D6F' },
  '& .MuiFormLabel-asterisk': { color: '#D32F2F' },
};

const valueSx = {
  color: '#000000',
  fontSize: '16px',
  lineHeight: '20px',
  letterSpacing: 0,
};

const menuPaperSx = {
  borderBottomLeftRadius: '16px',
  borderBottomRightRadius: '16px',
  backgroundColor: '#ffffff',
  boxShadow: '0px 0px 15px 0px rgba(0, 0, 0, 0.15)',
  mt: '4px',
  maxHeight: 280,
};

const CommonDropdown = ({
  label,
  value = [],
  onChange,
  options = [],
  required = false,
  disabled = false,
  error,
  multiple= false,
  errorMessage,
  placeholder,
}) => {
  const hasError = Boolean(error || errorMessage);
  const labelId = `dd-${String(label).replace(/\s+/g, '-').toLowerCase()}`;

  return (
    <Box sx={{ width: '100%', mt: '11px' }}>
      <FormControl variant="standard" fullWidth required={required} error={hasError}>
        <InputLabel id={labelId} shrink sx={labelSx}>
          {label}
        </InputLabel>
        <Select
          labelId={labelId}
          value={value ?? []}
          displayEmpty
          disabled={disabled}
          multiple={multiple}
          onChange={(e) => onChange?.(e.target.value)}
          renderValue={(selected) => {
            if (selected === '' || selected === undefined || selected === null) {
              return (
                <Typography sx={{ ...valueSx, color: '#9E9E9E' }}>
                  {placeholder || ''}
                </Typography>
              );
            }
            const opt = options.filter((o) => {
              if(typeof selected === "string"){
                return o.value === selected;
              }else{
                return selected.includes(o.value);
              }
              }
            );
            if (opt && opt.length > 2) {
              const labels = opt.map((o) => o.label);
              return (
                <Typography sx={valueSx}>
                  {`${labels.slice(0, 2).join(", ")} +${labels.length - 2}`}
                </Typography>
              );
            }
            return <Typography sx={valueSx}>{opt ? opt.map(o =>o.label).join(", ") : selected.join(",")}</Typography>;
          }}
          MenuProps={{
            PaperProps: { sx: menuPaperSx },
            anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
            transformOrigin: { vertical: 'top', horizontal: 'left' },
          }}
          sx={{
            mt: '5px !important',
            '& .MuiSelect-select': { ...valueSx, py: '6px' },
            '& .MuiSelect-icon': { color: '#000000' },
            '&:before': { borderBottomColor: hasError ? '#9E231B' : '#C4C4C4' },
            '&:hover:not(.Mui-disabled):before': { borderBottomColor: '#1460AA' },
            '&:after': { borderBottomColor: hasError ? '#9E231B' : '#1460AA' },
          }}
        >
          {options.map((opt) => (
            <MenuItem
              key={opt.value}
              value={opt.value}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: '16px',
                color: '#000000',
                '&.Mui-selected': { backgroundColor: 'rgba(20, 96, 170, 0.08)' },
                '&.Mui-selected:hover': { backgroundColor: 'rgba(20, 96, 170, 0.12)' },
              }}
            >
              <span>{opt.label}</span>
              {value.includes(opt.value) && (
                <CheckIcon sx={{ fontSize: 18, color: '#1460AA' }} />
              )}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {hasError && errorMessage && <FieldError message={errorMessage} />}
    </Box>
  );
};

export default CommonDropdown;
